import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { MapPin, Clock, DollarSign, Building2, Bookmark, BookmarkCheck, Share2, ArrowLeft, CheckCircle2, Briefcase, Users, Car, GraduationCap, Accessibility, Home, EyeOff, ChevronDown, ChevronUp, Calendar, FileText, Star, Target, ChevronLeft } from "lucide-react";
interface JobData {
  id: string;
  title: string;
  description: string;
  job_type: string[];
  seniority_level: string[] | null;
  contract_type: string[] | null;
  work_schedule: string[] | null;
  area: string | null;
  state: string | null;
  city: string | null;
  neighborhood: string | null;
  salary_range_start: number | null;
  salary_range_end: number | null;
  benefits: string[] | null;
  created_at: string;
  is_pcd_eligible: boolean | null;
  is_first_job: boolean | null;
  is_half_period: boolean | null;
  is_home_office: boolean | null;
  is_confidential: boolean | null;
  experience_preference: string | null;
  requirements: string[] | null;
  desirables: string[] | null;
  education_level: string | null;
  cnh_required: string[] | null;
  other_benefits: string | null;
  company_id: string;
  company_profiles: {
    id: string;
    company_name: string;
    logo_url: string | null;
    description: string | null;
    sector: string | null;
    employee_count: string | null;
  };
}
interface JobQuestion {
  id: string;
  question_text: string;
  question_type: string;
  options: string[] | null;
  is_required: boolean | null;
  order_index: number;
}
const EDUCATION_LABELS: Record<string, string> = {
  fundamental_incompleto: "Ensino Fundamental Incompleto",
  fundamental_completo: "Ensino Fundamental Completo",
  fundamental_cursando: "Ensino Fundamental Cursando",
  medio_incompleto: "Ensino Médio Incompleto",
  medio_completo: "Ensino Médio Completo",
  medio_cursando: "Ensino Médio Cursando",
  superior_incompleto: "Ensino Superior Incompleto",
  superior_completo: "Ensino Superior Completo",
  superior_cursando: "Ensino Superior Cursando",
  pos_graduacao: "Pós-graduação",
  mestrado: "Mestrado",
  doutorado: "Doutorado",
  indiferente: "Indiferente"
};
const BENEFITS_LABELS: Record<string, string> = {
  vt: "Vale Transporte",
  vr: "Vale Refeição",
  va: "Vale Alimentação",
  plano_saude: "Plano de Saúde",
  plano_odonto: "Plano Odontológico",
  home_office: "Auxílio Home Office",
  educacao: "Auxílio Educação",
  bonus_plr: "Bônus / PLR",
  gympass: "Gympass / Academia",
  day_off: "Day Off (Aniversário)",
  outros: "Outros Benefícios"
};
// Normaliza string removendo acentos e convertendo para minúsculas
const normalizeString = (str: string): string => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const formatBenefit = (benefit: string): string => {
  const normalized = normalizeString(benefit);
  return BENEFITS_LABELS[normalized] || BENEFITS_LABELS[benefit] || benefit.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
const WORK_SCHEDULE_LABELS: Record<string, string> = {
  comercial: "Comercial",
  turnos: "Turnos",
  flexivel: "Flexível",
  meio_periodo: "Meio Período"
};
const formatWorkSchedule = (schedule: string): string => {
  const normalized = normalizeString(schedule);
  return WORK_SCHEDULE_LABELS[normalized] || WORK_SCHEDULE_LABELS[schedule] || schedule.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
const JOB_TYPE_LABELS: Record<string, string> = {
  presencial: "Presencial",
  hibrido: "Híbrido",
  remoto: "Remoto"
};
const formatJobType = (type: string): string => {
  const normalized = normalizeString(type);
  return JOB_TYPE_LABELS[normalized] || JOB_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
const CONTRACT_TYPE_LABELS: Record<string, string> = {
  clt: "CLT",
  pj: "PJ",
  estagio: "Estágio",
  temporario: "Temporário",
  freelancer: "Freelancer",
  consultor: "Consultor"
};
const formatContractType = (type: string): string => {
  const normalized = normalizeString(type);
  return CONTRACT_TYPE_LABELS[normalized] || CONTRACT_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const AREA_LABELS: Record<string, string> = {
  ti: "TI / Tecnologia",
  rh: "Recursos Humanos",
  marketing: "Marketing",
  financeiro: "Financeiro",
  comercial: "Comercial / Vendas",
  operacoes: "Operações",
  juridico: "Jurídico",
  administrativo: "Administrativo",
  logistica: "Logística",
  producao: "Produção / Industrial",
  atendimento: "Atendimento ao Cliente",
  outros: "Outros"
};
const formatArea = (area: string): string => {
  const normalized = normalizeString(area);
  return AREA_LABELS[normalized] || AREA_LABELS[area] || area.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
const VagaDetailPage = () => {
  const {
    id
  } = useParams();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [questions, setQuestions] = useState<JobQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [hasApplied, setHasApplied] = useState(false);
  const [candidateProfileId, setCandidateProfileId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Verifica se todas as perguntas obrigatórias foram respondidas
  const allRequiredAnswered = questions
    .filter(q => q.is_required)
    .every(q => answers[q.id]?.trim());
  useEffect(() => {
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setSession(session);
    });
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);
  useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;
      const {
        data,
        error
      } = await supabase.from("jobs").select(`
          *,
          company_profiles (
            id,
            company_name,
            logo_url,
            description,
            sector,
            employee_count
          )
        `).eq("id", id).eq("is_active", true).maybeSingle();
      if (error) {
        console.error("Error fetching job:", error);
      }
      setJob(data);
      setLoading(false);
    };
    fetchJob();
  }, [id]);
  useEffect(() => {
    const checkApplicationAndSaved = async () => {
      if (!session?.user?.id || !id) return;
      const {
        data: profile
      } = await supabase.from("candidate_profiles").select("id").eq("user_id", session.user.id).maybeSingle();
      if (profile) {
        setCandidateProfileId(profile.id);
        const {
          data: existingApp
        } = await supabase.from("applications").select("id").eq("job_id", id).eq("candidate_id", profile.id).maybeSingle();
        setHasApplied(!!existingApp);
        const {
          data: savedJob
        } = await supabase.from("saved_jobs").select("id").eq("job_id", id).eq("candidate_id", profile.id).maybeSingle();
        setIsSaved(!!savedJob);
      }
    };
    checkApplicationAndSaved();
  }, [session, id]);
  const checkAuthentication = () => {
    if (!session) {
      toast({
        title: "Login necessário",
        description: "Você precisa fazer login para realizar esta ação.",
        variant: "destructive"
      });
      navigate("/entrar");
      return false;
    }
    return true;
  };
  const handleApply = async () => {
    if (!checkAuthentication()) return;
    if (!candidateProfileId) {
      toast({
        title: "Perfil de candidato necessário",
        description: "Você precisa ter um perfil de candidato para se candidatar.",
        variant: "destructive"
      });
      return;
    }
    if (hasApplied) {
      toast({
        title: "Já candidatado",
        description: "Você já se candidatou a esta vaga."
      });
      return;
    }
    setLoadingQuestions(true);
    const {
      data: jobQuestions,
      error
    } = await supabase.from("job_questions").select("*").eq("job_id", id).order("order_index", {
      ascending: true
    });
    if (error) {
      console.error("Error fetching questions:", error);
    }
    setQuestions(jobQuestions || []);
    setAnswers({});
    setLoadingQuestions(false);
    setShowApplyModal(true);
  };
  const submitApplication = async () => {
    for (const question of questions) {
      if (question.is_required && !answers[question.id]?.trim()) {
        toast({
          title: "Resposta obrigatória",
          description: `Por favor, responda: "${question.question_text}"`,
          variant: "destructive"
        });
        return;
      }
    }
    setSubmitting(true);
    try {
      const {
        data: application,
        error: appError
      } = await supabase.from("applications").insert({
        job_id: id,
        candidate_id: candidateProfileId
      }).select("id").single();
      if (appError) throw appError;
      if (questions.length > 0 && application) {
        const answersToInsert = questions.filter(q => answers[q.id]?.trim()).map(q => ({
          application_id: application.id,
          question_id: q.id,
          answer_text: answers[q.id].trim()
        }));
        if (answersToInsert.length > 0) {
          const {
            error: answersError
          } = await supabase.from("application_answers").insert(answersToInsert);
          if (answersError) throw answersError;
        }
      }
      setShowApplyModal(false);
      setHasApplied(true);
      toast({
        title: "Candidatura enviada!",
        description: "Você se candidatou com sucesso a esta vaga."
      });
    } catch (error: any) {
      console.error("Error submitting application:", error);
      toast({
        title: "Erro ao candidatar",
        description: "Não foi possível enviar sua candidatura. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };
  const handleSave = async () => {
    if (!checkAuthentication()) return;
    if (!candidateProfileId) {
      toast({
        title: "Perfil de candidato necessário",
        description: "Você precisa ter um perfil de candidato para salvar vagas.",
        variant: "destructive"
      });
      return;
    }
    try {
      if (isSaved) {
        const {
          error
        } = await supabase.from("saved_jobs").delete().eq("job_id", id).eq("candidate_id", candidateProfileId);
        if (error) throw error;
        setIsSaved(false);
        toast({
          title: "Vaga removida",
          description: "A vaga foi removida dos seus salvos."
        });
      } else {
        const {
          error
        } = await supabase.from("saved_jobs").insert({
          job_id: id,
          candidate_id: candidateProfileId
        });
        if (error) throw error;
        setIsSaved(true);
        toast({
          title: "Vaga salva!",
          description: "Você pode ver suas vagas salvas em 'Vagas Salvas'."
        });
      }
    } catch (error: any) {
      console.error("Error saving/unsaving job:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar/remover a vaga. Tente novamente.",
        variant: "destructive"
      });
    }
  };
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: "Link copiado!",
      description: "O link da vaga foi copiado para a área de transferência."
    });
  };
  const formatSalary = () => {
    if (!job?.salary_range_start && !job?.salary_range_end) {
      return "A definir";
    }
    if (job.salary_range_start && job.salary_range_end) {
      return `R$ ${job.salary_range_start.toLocaleString("pt-BR")} - R$ ${job.salary_range_end.toLocaleString("pt-BR")}`;
    }
    if (job.salary_range_start) {
      return `A partir de R$ ${job.salary_range_start.toLocaleString("pt-BR")}`;
    }
    return `Até R$ ${job?.salary_range_end?.toLocaleString("pt-BR")}`;
  };
  const formatLocation = () => {
    if (job?.job_type?.some(t => t.toLowerCase() === 'remoto')) {
      if (job.city || job.state) {
        return `Remoto • ${[job.city, job.state].filter(Boolean).join(", ")}`;
      }
      return "Remoto";
    }
    const parts = [job?.city, job?.state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Não informado";
  };
  const formatPostedDate = () => {
    if (!job?.created_at) return "";
    const date = new Date(job.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Hoje";
    if (diffDays === 1) return "Ontem";
    if (diffDays < 7) return `Há ${diffDays} dias`;
    if (diffDays < 30) return `Há ${Math.floor(diffDays / 7)} semana(s)`;
    return date.toLocaleDateString("pt-BR");
  };

  const isNewJob = () => {
    if (!job?.created_at) return false;
    const date = new Date(job.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };
  const getCompanyName = () => {
    if (job?.is_confidential) {
      return "Empresa Confidencial";
    }
    return job?.company_profiles?.company_name || "Empresa";
  };

  // Get special badges (max 4)
  const getSpecialBadges = () => {
    const badges: {
      label: string;
      icon?: React.ReactNode;
      variant: "new" | "secondary" | "outline";
    }[] = [];
    if (isNewJob()) {
      badges.push({
        label: "Nova",
        variant: "new"
      });
    }
    if (job?.is_pcd_eligible) {
      badges.push({
        label: "PCD",
        icon: <Accessibility className="h-3 w-3" />,
        variant: "secondary"
      });
    }
    if (job?.is_first_job) {
      badges.push({
        label: "1° Emprego",
        variant: "secondary"
      });
    }
    if (job?.is_home_office) {
      badges.push({
        label: "Home-office",
        icon: <Home className="h-3 w-3" />,
        variant: "secondary"
      });
    }
    return badges.slice(0, 4);
  };

  // Benefits display logic
  const INITIAL_BENEFITS_COUNT = 4;
  const benefits = job?.benefits || [];
  const visibleBenefits = showAllBenefits ? benefits : benefits.slice(0, INITIAL_BENEFITS_COUNT);
  const hasMoreBenefits = benefits.length > INITIAL_BENEFITS_COUNT;
  if (loading) {
    return <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="container py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <Skeleton className="h-8 w-32 mb-6" />
                <Card className="mb-6">
                  <CardContent className="p-6 lg:p-8">
                    <Skeleton className="h-10 w-3/4 mb-4" />
                    <Skeleton className="h-5 w-1/2 mb-6" />
                    <div className="flex gap-2 mb-6">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-6 w-16" />
                    </div>
                    <Skeleton className="h-12 w-full mb-6" />
                    <Skeleton className="h-40 w-full" />
                  </CardContent>
                </Card>
              </div>
              <aside className="lg:w-80">
                <Skeleton className="h-32 w-full mb-4" />
                <Skeleton className="h-48 w-full" />
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>;
  }
  if (!job) {
    return <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-display font-bold text-foreground mb-4">Vaga não encontrada</h1>
            <p className="text-muted-foreground mb-6">Esta vaga pode ter sido removida ou não está mais disponível.</p>
            <Button asChild>
              <Link to="/vagas">Ver todas as vagas</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>;
  }
  const specialBadges = getSpecialBadges();
  return <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 pb-24 lg:pb-0">
        {/* Breadcrumb */}
        <div className="border-b border-border bg-card">
          <div className="container py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <span>/</span>
              <Link to="/vagas" className="hover:text-foreground transition-colors">Vagas</Link>
              <span>/</span>
              <span className="text-foreground truncate max-w-[200px]">{job.title}</span>
            </div>
          </div>
        </div>

        <div className="container py-6 lg:py-8 pb-0">
          <div className="mobile-container md:w-full flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <Button variant="ghost" size="sm" className="mb-4" asChild>
                <Link to="/vagas" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Link>
              </Button>

              {/* Hero Header Card */}
              <Card className="mb-6 overflow-hidden">
                <CardContent className="p-0">
                  {/* Header Section */}
                  <div className="p-6 lg:p-8 bg-gradient-to-br from-primary/5 via-background to-background">
                    <div className="flex flex-col md:flex-row md:items-start gap-4 mb-4">
                      {/* Company Logo */}
                      <div className="h-14 w-14 rounded-xl bg-card border flex items-center justify-center flex-shrink-0 shadow-sm">
                        {job.is_confidential ? <EyeOff className="h-7 w-7 text-muted-foreground" /> : job.company_profiles?.logo_url ? <img src={job.company_profiles.logo_url} alt={job.company_profiles.company_name} className="h-10 w-10 object-contain" /> : <Building2 className="h-7 w-7 text-muted-foreground" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Special Badges */}
                        {specialBadges.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">
                            {specialBadges.map((badge, index) => <Badge key={index} variant={badge.variant} className="gap-1 text-xs">
                                {badge.icon}
                                {badge.label}
                              </Badge>)}
                          </div>}

                        {/* Title */}
                        <h1 className="text-2xl lg:text-3xl font-display font-bold text-foreground mb-1 leading-tight">
                          {job.title}
                        </h1>
                        
                        {/* Company & Location */}
                        <p className="text-base text-muted-foreground">
                          {getCompanyName()} • {formatLocation()}
                        </p>
                      </div>

                      {/* Desktop: Share/Save */}
                      <div className="hidden md:flex gap-1">
                        <Button variant="ghost" size="icon" onClick={handleShare} title="Compartilhar">
                          <Share2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>

                    {/* Key Info Summary - Grid Layout */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border pt-4 mt-2">
                      {/* Salary */}
                      <div className="flex items-start gap-2">
                        <DollarSign className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div>
                          <span className="text-xs text-muted-foreground">Salário</span>
                          <p className="font-medium text-foreground text-sm">{formatSalary()}</p>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div>
                          <span className="text-xs text-muted-foreground">Localização</span>
                          <p className="font-medium text-foreground text-sm">{formatLocation()}</p>
                        </div>
                      </div>

                      {/* Job Type (Work Model) */}
                      {job.job_type && job.job_type.length > 0 && <div className="flex items-start gap-2">
                          <Home className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <div>
                            <span className="text-xs text-muted-foreground">Modelo de Trabalho</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {job.job_type.map((type, idx) => <Badge key={idx} variant="secondary" className="text-xs">
                                  {formatJobType(type)}
                                </Badge>)}
                            </div>
                          </div>
                        </div>}

                      {/* Contract Type */}
                      {job.contract_type && job.contract_type.length > 0 && <div className="flex items-start gap-2">
                          <Briefcase className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <div>
                            <span className="text-xs text-muted-foreground">Regime de Contratação</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {job.contract_type.map((type, idx) => <Badge key={idx} variant="secondary" className="text-xs">
                                  {formatContractType(type)}
                                </Badge>)}
                            </div>
                          </div>
                        </div>}

                      {/* Work Schedule */}
                      {job.work_schedule && job.work_schedule.length > 0 && <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <div>
                            <span className="text-xs text-muted-foreground">Horário de Trabalho</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {job.work_schedule.map((schedule, idx) => <Badge key={idx} variant="secondary" className="text-xs">
                                  {formatWorkSchedule(schedule)}
                                </Badge>)}
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Content Sections */}
              <div className="space-y-6">
                {/* Job Description */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-display font-semibold text-foreground">
                        Sobre a Vaga
                      </h2>
                    </div>
                    <div className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {job.description}
                    </div>
                  </CardContent>
                </Card>

                {/* Requirements */}
                {job.requirements && job.requirements.length > 0 && <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Target className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-display font-semibold text-foreground">
                          Requisitos Obrigatórios
                        </h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {job.requirements.map((req, index) => <Badge key={index} variant="secondary" className="text-sm py-1.5 px-3">
                            {req}
                          </Badge>)}
                      </div>
                    </CardContent>
                  </Card>}

                {/* Desirables */}
                {job.desirables && job.desirables.length > 0 && <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-display font-semibold text-foreground">
                          Diferenciais
                        </h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {job.desirables.map((des, index) => <Badge key={index} variant="outline" className="text-sm py-1.5 px-3">
                            {des}
                          </Badge>)}
                      </div>
                    </CardContent>
                  </Card>}

                {/* Education & Experience */}
                {(job.education_level || job.experience_preference || job.cnh_required && job.cnh_required.length > 0) && <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <GraduationCap className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-display font-semibold text-foreground">
                          Formação e Experiência
                        </h2>
                      </div>
                      <ul className="space-y-3">
                        {job.education_level && job.education_level !== "indiferente" && <li className="flex items-start gap-3 text-muted-foreground">
                            <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>Escolaridade mínima: <span className="text-foreground font-medium">{EDUCATION_LABELS[job.education_level] || job.education_level}</span></span>
                          </li>}
                        {job.experience_preference && job.experience_preference !== "indiferente" && <li className="flex items-start gap-3 text-muted-foreground">
                            <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>
                              {job.experience_preference === "com_experiencia" ? "Necessária experiência prévia" : "Aceita candidatos sem experiência"}
                            </span>
                          </li>}
                        {job.cnh_required && job.cnh_required.length > 0 && <li className="flex items-start gap-3 text-muted-foreground">
                            <Car className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>CNH: <span className="text-foreground font-medium">{job.cnh_required.join(", ")}</span></span>
                          </li>}
                      </ul>
                    </CardContent>
                  </Card>}

                {/* Benefits - Compact with expansion */}
                {benefits.length > 0 && <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                          <h2 className="text-lg font-display font-semibold text-foreground">
                            Benefícios
                          </h2>
                          <span className="text-sm text-muted-foreground">({benefits.length})</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {visibleBenefits.map((benefit, index) => <Badge key={index} variant="outline" className="text-sm py-1.5 px-3 bg-primary/5 border-primary/20">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-primary" />
                            {formatBenefit(benefit)}
                          </Badge>)}
                      </div>
                      
                      {hasMoreBenefits && <Button variant="ghost" size="sm" className="mt-3 text-primary hover:text-primary" onClick={() => setShowAllBenefits(!showAllBenefits)}>
                          {showAllBenefits ? <>
                              <ChevronUp className="h-4 w-4 mr-1" />
                              Mostrar menos
                            </> : <>
                              <ChevronDown className="h-4 w-4 mr-1" />
                              Ver todos os {benefits.length} benefícios
                            </>}
                        </Button>}

                      {job.other_benefits && <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                          <strong className="text-foreground">Outros:</strong> {job.other_benefits}
                        </p>}
                    </CardContent>
                  </Card>}

                {/* Work Schedule - only if not already shown */}
                {job.work_schedule && job.work_schedule.length > 1}

                {/* Company Card - Mobile/Tablet Only */}
                <Card className="lg:hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Sobre a Empresa</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {job.is_confidential ? (
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">Confidencial</p>
                        </div>
                      </div>
                    ) : (
                      <Link to={`/empresas/${job.company_id}/vagas`} className="flex items-center gap-3 mb-3 group hover:opacity-80 transition-opacity">
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                          {job.company_profiles?.logo_url ? (
                            <img src={job.company_profiles.logo_url} alt={job.company_profiles.company_name} className="h-9 w-9 object-contain rounded" />
                          ) : (
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {job.company_profiles?.company_name}
                          </p>
                          {job.company_profiles?.sector && (
                            <p className="text-xs text-muted-foreground truncate">{job.company_profiles.sector}</p>
                          )}
                        </div>
                      </Link>
                    )}
                    {job.company_profiles?.employee_count && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Users className="h-4 w-4" />
                        {job.company_profiles.employee_count} funcionários
                      </div>
                    )}
                    {!job.is_confidential && job.company_profiles?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {job.company_profiles.description}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Info Card - Mobile/Tablet Only */}
                <Card className="lg:hidden">
                  <CardContent className="p-5">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>Publicada</span>
                        </div>
                        <span className="text-foreground font-medium">{formatPostedDate()}</span>
                      </div>
                      {job.area && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Briefcase className="h-4 w-4" />
                            <span>Área</span>
                          </div>
                          <span className="text-foreground font-medium truncate max-w-[200px]">{formatArea(job.area)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0 hidden lg:block">
              <div className="sticky top-24 space-y-4">
                {/* CTA Card */}
                <Card className="border-primary/20">
                  <CardContent className="p-5">
                    <Button className="w-full mb-3" size="lg" onClick={handleApply} disabled={hasApplied || loadingQuestions} variant={hasApplied ? "secondary" : "default"}>
                      {loadingQuestions ? "Carregando..." : hasApplied ? "Candidatura Enviada ✓" : "Candidatar-se"}
                    </Button>
                    <Button variant="outline" className="w-full gap-2" onClick={handleSave}>
                      {isSaved ? <>
                          <BookmarkCheck className="h-4 w-4 fill-current" />
                          Vaga Salva
                        </> : <>
                          <Bookmark className="h-4 w-4" />
                          Salvar Vaga
                        </>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Company Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Sobre a Empresa</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {job.is_confidential ? (
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">Confidencial</p>
                        </div>
                      </div>
                    ) : (
                      <Link to={`/empresas/${job.company_id}/vagas`} className="flex items-center gap-3 mb-3 group hover:opacity-80 transition-opacity">
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                          {job.company_profiles?.logo_url ? <img src={job.company_profiles.logo_url} alt={job.company_profiles.company_name} className="h-9 w-9 object-contain rounded" /> : <Building2 className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">{job.company_profiles?.company_name}</p>
                          {job.company_profiles?.sector && <p className="text-xs text-muted-foreground truncate">{job.company_profiles.sector}</p>}
                        </div>
                      </Link>
                    )}
                    {job.company_profiles?.employee_count && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Users className="h-4 w-4" />
                        {job.company_profiles.employee_count} funcionários
                      </div>
                    )}
                    {!job.is_confidential && job.company_profiles?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {job.company_profiles.description}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Info Card */}
                <Card>
                  <CardContent className="p-5">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>Publicada</span>
                        </div>
                        <span className="text-foreground font-medium">{formatPostedDate()}</span>
                      </div>
                      {job.area && <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Briefcase className="h-4 w-4" />
                            <span>Área</span>
                          </div>
                          <span className="text-foreground font-medium truncate max-w-[140px]">{formatArea(job.area)}</span>
                        </div>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Mobile Fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-background/95 backdrop-blur-lg border-t p-4 z-50 safe-area-inset-bottom">
        <div className="flex gap-3 max-w-lg mx-auto">
          <Button className="flex-1" size="lg" onClick={handleApply} disabled={hasApplied || loadingQuestions} variant={hasApplied ? "secondary" : "default"}>
            {loadingQuestions ? "Carregando..." : hasApplied ? "Candidatura Enviada ✓" : "Candidatar-se"}
          </Button>
          <Button variant="outline" size="lg" onClick={handleSave} className="px-4">
            {isSaved ? <BookmarkCheck className="h-5 w-5 fill-current" /> : <Bookmark className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <Footer />

      {/* Application Modal - Sheet for Mobile, Dialog for Desktop */}
      {isMobile ? (
        <Sheet open={showApplyModal} onOpenChange={setShowApplyModal}>
          <SheetContent side="right" className="w-full sm:w-[95vw] p-0 flex flex-col">
            <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => setShowApplyModal(false)} className="shrink-0">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <SheetTitle className="text-left text-lg font-semibold break-words leading-tight">
                  Perguntas da vaga
                </SheetTitle>
              </div>
            </SheetHeader>
            
            <div className="flex-1 overflow-y-auto p-4">
              {questions.length === 0 ? (
                <p className="text-muted-foreground py-4">
                  Clique em confirmar para enviar sua candidatura.
                </p>
              ) : (
                <div className="space-y-6">
                  {questions.map((question, index) => (
                    <div key={question.id} className="space-y-2">
                      <Label className="text-base">
                        {index + 1}. {question.question_text}
                        {question.is_required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      
                      {question.question_type === "curta" && (
                        <Input 
                          value={answers[question.id] || ""} 
                          onChange={e => setAnswers(prev => ({
                            ...prev,
                            [question.id]: e.target.value
                          }))} 
                          placeholder="Sua resposta..." 
                        />
                      )}
                      
                      {question.question_type === "aberta" && (
                        <Textarea 
                          value={answers[question.id] || ""} 
                          onChange={e => setAnswers(prev => ({
                            ...prev,
                            [question.id]: e.target.value
                          }))} 
                          placeholder="Sua resposta..." 
                          rows={4} 
                        />
                      )}
                      
                      {question.question_type === "multipla_escolha" && question.options && (
                        <RadioGroup 
                          value={answers[question.id] || ""} 
                          onValueChange={value => setAnswers(prev => ({
                            ...prev,
                            [question.id]: value
                          }))}
                        >
                          {question.options.map(option => (
                            <div key={option} className="flex items-center space-x-2">
                              <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                              <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer">
                                {option}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-background sticky bottom-0">
              <div className="flex flex-col gap-3">
                <Button onClick={submitApplication} disabled={submitting || !allRequiredAnswered} className="w-full">
                  {submitting ? "Enviando..." : "Confirmar Candidatura"}
                </Button>
                <Button variant="outline" onClick={() => setShowApplyModal(false)} className="w-full">
                  Cancelar
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="break-words pr-8">Candidatar-se à vaga: {job.title}</DialogTitle>
            </DialogHeader>
            
            {questions.length === 0 ? (
              <p className="text-muted-foreground py-4">
                Clique em confirmar para enviar sua candidatura.
              </p>
            ) : (
              <div className="space-y-6 py-4">
                {questions.map((question, index) => (
                  <div key={question.id} className="space-y-2">
                    <Label className="text-base">
                      {index + 1}. {question.question_text}
                      {question.is_required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    
                    {question.question_type === "curta" && (
                      <Input 
                        value={answers[question.id] || ""} 
                        onChange={e => setAnswers(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))} 
                        placeholder="Sua resposta..." 
                      />
                    )}
                    
                    {question.question_type === "aberta" && (
                      <Textarea 
                        value={answers[question.id] || ""} 
                        onChange={e => setAnswers(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))} 
                        placeholder="Sua resposta..." 
                        rows={4} 
                      />
                    )}
                    
                    {question.question_type === "multipla_escolha" && question.options && (
                      <RadioGroup 
                        value={answers[question.id] || ""} 
                        onValueChange={value => setAnswers(prev => ({
                          ...prev,
                          [question.id]: value
                        }))}
                      >
                        {question.options.map(option => (
                          <div key={option} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                            <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                Cancelar
              </Button>
              <Button onClick={submitApplication} disabled={submitting || !allRequiredAnswered}>
                {submitting ? "Enviando..." : "Confirmar Candidatura"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>;
};
export default VagaDetailPage;